import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { from, switchMap, catchError } from 'rxjs';
import { environment } from '@env/environment';
import { FirebaseAppCheckService } from '@core/services/firebase/app-check.service';

/**
 * Interceptor to add a Firebase App Check attestation token to outgoing requests
 * when the application is running on a mobile platform (Android or iOS).
 */
export const platformInterceptor: HttpInterceptorFn = (req, next) => {
  const appCheckService = inject(FirebaseAppCheckService);
  const platform = Capacitor.getPlatform();
  const isMobile = platform === 'android' || platform === 'ios';

  // Only add the header if running on mobile and targeting internal APIs
  const isInternalRequest =
    req.url.startsWith(environment.apiBaseUrl) ||
    req.url.startsWith(environment.apiAuthUrl) ||
    req.url.startsWith(environment.apiAccountUrl);

  if (isMobile && isInternalRequest) {
    // If App Check has permanently failed for this session (e.g., 403 or too many attempts),
    // skip the token injection and proceed without the header.
    if (appCheckService.isAppCheckSuspended()) {
      return next(req);
    }

    // We use 'from' to convert the Promise returned by getToken() into an Observable
    // appCheckService.getToken() ensures that initialization is complete before fetching the token
    return from(appCheckService.getToken()).pipe(
      catchError((error) => {
        // Fallback: If App Check fails, we still allow the request to proceed (backend will likely reject it)
        console.error('Firebase App Check Error:', error);
        return from([null]);
      }),
      switchMap((result) => {
        if (result && result.token) {
          const platformReq = req.clone({
            setHeaders: {
              'X-Firebase-AppCheck': result.token,
            },
          });
          return next(platformReq);
        }
        return next(req);
      })
    );
  }

  return next(req);
};
